import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedLayout } from '../components/layout/ProtectedLayout';
import { listEvents, createEvent, updateEvent, deleteEvent, type EventItem } from '../api/events';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';
import { Calendar } from '../components/ui/calendar';

const isoLocal = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,16);

const CalendarPage: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [form, setForm] = useState<Partial<EventItem>>({
    title: '',
    description: '',
    start: isoLocal(new Date()),
    end: isoLocal(new Date(Date.now()+60*60*1000)),
  });
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await listEvents();
        setEvents(data);
      } catch (e: any) {
        setError(e.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dayEvents = useMemo(() => {
    const key = format(selectedDate, 'yyyy-MM-dd');
    return events.filter(e => (e.start || '').slice(0,10) === key || (e.end || '').slice(0,10) === key);
  }, [events, selectedDate]);

  const resetForm = () => {
    setForm({ title: '', description: '', start: isoLocal(selectedDate), end: isoLocal(new Date(selectedDate.getTime()+60*60*1000)) });
    setEditingId(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload: EventItem = {
        title: form.title || '',
        description: form.description || '',
        start: new Date(form.start as string).toISOString(),
        end: new Date(form.end as string).toISOString(),
      };
      if (editingId) {
        const updated = await updateEvent(editingId, payload);
        setEvents(prev => prev.map(ev => ev.id === editingId ? updated : ev));
      } else {
        const created = await createEvent(payload);
        setEvents(prev => [...prev, created]);
      }
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to save event');
    }
  };

  const onEdit = (ev: EventItem) => {
    setEditingId(ev.id!);
    setForm({
      title: ev.title,
      description: ev.description,
      start: isoLocal(new Date(ev.start)),
      end: isoLocal(new Date(ev.end))
    });
  };

  const onDelete = async (id: number) => {
    setError(null);
    try {
      await deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      if (editingId === id) resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to delete event');
    }
  };

  return (
    <ProtectedLayout>
      {() => (
        <div className="space-y-8">
          <header className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h1 className="text-3xl font-semibold text-foreground">Calendar</h1>
            <p className="mt-2 text-sm text-muted-foreground">Create and manage events.</p>
          </header>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-xl border bg-card p-4 md:col-span-1">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d: Date | undefined) => d && setSelectedDate(new Date(d))}
                className="rounded-md border"
              />
            </div>

            <div className="rounded-xl border bg-card p-4 md:col-span-2 space-y-4">
              <h2 className="font-semibold">{editingId ? 'Edit Event' : 'Add Event'}</h2>
              {error && <div className="text-sm text-red-600">{error}</div>}
              <form className="space-y-3" onSubmit={onSubmit}>
                <div>
                  <label className="text-sm">Title</label>
                  <Input value={form.title as string} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} required />
                </div>
                <div>
                  <label className="text-sm">Description</label>
                  <Input value={form.description as string} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm">Start</label>
                    <Input type="datetime-local" value={form.start as string} onChange={(e) => setForm(f => ({...f, start: e.target.value}))} required />
                  </div>
                  <div>
                    <label className="text-sm">End</label>
                    <Input type="datetime-local" value={form.end as string} onChange={(e) => setForm(f => ({...f, end: e.target.value}))} required />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={loading}>{editingId ? 'Update' : 'Create'}</Button>
                  {editingId && <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>}
                </div>
              </form>
            </div>
          </div>

          <section className="rounded-xl border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold">Events on {format(selectedDate, 'PP')}</h2>
              {loading && <span className="text-sm text-muted-foreground">Loading…</span>}
            </div>
            {dayEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events.</p>
            ) : (
              <ul className="divide-y">
                {dayEvents.map(ev => (
                  <li key={ev.id} className="py-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{ev.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(ev.start), 'p')} – {format(new Date(ev.end), 'p')}
                        {ev.created_by ? ` · by ${ev.created_by}` : ''}
                      </div>
                      {ev.description && <div className="text-sm">{ev.description}</div>}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={() => onEdit(ev)}>Edit</Button>
                      {ev.id && <Button variant="destructive" onClick={() => onDelete(ev.id!)}>Delete</Button>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </ProtectedLayout>
  );
};

export default CalendarPage;
